import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { BookOpen, Plus, Trash2, RotateCcw, Loader2, ChevronDown, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { getMyCurriculum, updateMyCurriculum, resetMyCurriculum } from '../lib/api';

const DISCIPLINES = [
  { id: 'jiujitsu', label: 'Brazilian Jiu-Jitsu' },
  { id: 'self_defense', label: 'Self-Defense' },
  { id: 'personal_training', label: 'Personal Training' },
];

const BELT_OPTIONS = ['white', 'blue', 'purple', 'brown', 'black'];

const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

const CurriculumEditor = () => {
  const [active, setActive] = useState('jiujitsu');
  const [data, setData] = useState(null);
  const [phases, setPhases] = useState([]);
  const [openPhase, setOpenPhase] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const res = await getMyCurriculum();
      setData(res.data);
      setPhases(res.data?.disciplines?.[active] || []);
    } catch (e) { toast.error('Could not load curriculum'); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);
  useEffect(() => {
    if (data) setPhases(data.disciplines?.[active] || []);
    setOpenPhase(null);
  }, [active, data]);

  const updatePhase = (idx, patch) => setPhases((arr) => arr.map((p, i) => i === idx ? { ...p, ...patch } : p));

  const addPhase = () => {
    setPhases((arr) => [...arr, {
      id: `tmp_${Date.now()}`,
      belt: 'white',
      title: 'New phase',
      tagline: '',
      focus: '',
      outcome: '',
      challenges: [],
    }]);
    setOpenPhase(phases.length);
  };

  const removePhase = (idx) => {
    if (!window.confirm('Delete this phase and its challenges?')) return;
    setPhases((arr) => arr.filter((_, i) => i !== idx));
  };

  const addChallenge = (idx) => {
    setPhases((arr) => arr.map((p, i) => i !== idx ? p : ({
      ...p,
      challenges: [...(p.challenges || []), { id: `tmp_${Date.now()}`, description: '', xp_reward: 25 }],
    })));
  };

  const updateChallenge = (idx, cIdx, patch) => {
    setPhases((arr) => arr.map((p, i) => i !== idx ? p : ({
      ...p,
      challenges: (p.challenges || []).map((c, k) => k === cIdx ? { ...c, ...patch } : c),
    })));
  };

  const removeChallenge = (idx, cIdx) => {
    setPhases((arr) => arr.map((p, i) => i !== idx ? p : ({
      ...p,
      challenges: (p.challenges || []).filter((_, k) => k !== cIdx),
    })));
  };

  const save = async () => {
    setBusy(true);
    try {
      // clamp XP locally before sending
      const cleaned = phases.map((p) => ({
        ...p,
        challenges: (p.challenges || []).map((c) => ({ ...c, xp_reward: clamp(Number(c.xp_reward) || 1, 1, 100) })),
      }));
      const res = await updateMyCurriculum(active, cleaned);
      setPhases(res.data?.phases || cleaned);
      toast.success('Curriculum saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setBusy(false); }
  };

  const resetToDefault = async () => {
    if (!window.confirm('Reset this discipline\u2019s curriculum to DojoDash defaults? Your custom phases for this discipline will be lost.')) return;
    setBusy(true);
    try {
      const res = await resetMyCurriculum(active);
      setPhases(res.data?.phases || []);
      toast.success('Reset to default');
    } catch (e) {
      toast.error('Reset failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-[#18181B] border border-zinc-800 rounded-lg p-6" data-testid="curriculum-editor">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-2">
        <div className="flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-[#C0A062]" />
          <h2 className="text-lg font-bold text-white">Curriculum</h2>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="ghost" onClick={resetToDefault} disabled={busy} className="text-zinc-400 hover:text-white" data-testid="curriculum-reset-btn">
            <RotateCcw className="w-4 h-4 mr-1" /> Reset to default
          </Button>
          <Button size="sm" onClick={save} disabled={busy} className="bg-[#C0A062] text-black hover:bg-[#a98a52] font-semibold" data-testid="curriculum-save-btn">
            {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Save
          </Button>
        </div>
      </div>
      <p className="text-xs text-zinc-500 mb-4">
        Every coach starts with our default 5-phase system. Edit any phase, add or remove challenges,
        or add new phases. Advancement rules (6-session minimum + coach sign-off) are platform-locked.
      </p>

      {/* Discipline tabs */}
      <div className="flex flex-wrap gap-2 mb-5" data-testid="curriculum-discipline-tabs">
        {DISCIPLINES.map((d) => (
          <button
            key={d.id}
            type="button"
            onClick={() => setActive(d.id)}
            className={`text-xs px-3 py-1.5 rounded-full border ${
              active === d.id
                ? 'bg-[#C0A062]/15 border-[#C0A062]/60 text-[#C0A062]'
                : 'border-zinc-700 text-zinc-400 hover:bg-zinc-800'
            }`}
            data-testid={`curriculum-tab-${d.id}`}
          >
            {d.label}
          </button>
        ))}
      </div>

      {/* Phases */}
      <div className="space-y-3" data-testid="curriculum-phases">
        {phases.length === 0 ? (
          <p className="text-sm text-zinc-500">No phases for this discipline yet.</p>
        ) : phases.map((p, idx) => {
          const isOpen = openPhase === idx;
          return (
            <div key={p.id} className="bg-[#0d0d10] border border-zinc-800 rounded-md overflow-hidden" data-testid={`phase-${idx}`}>
              <button
                type="button"
                onClick={() => setOpenPhase(isOpen ? null : idx)}
                className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-zinc-900/40"
                data-testid={`phase-toggle-${idx}`}
              >
                {isOpen ? <ChevronDown className="w-4 h-4 text-zinc-500" /> : <ChevronRight className="w-4 h-4 text-zinc-500" />}
                <span className="text-xs uppercase tracking-wider text-[#C0A062] font-semibold w-14">{p.belt || 'belt'}</span>
                <span className="text-sm text-white font-semibold flex-1 truncate">{p.title || 'Untitled phase'}</span>
                <span className="text-xs text-zinc-500">{(p.challenges || []).length} challenges</span>
              </button>
              {isOpen && (
                <div className="px-4 pb-4 pt-2 border-t border-zinc-800 space-y-3">
                  <div className="grid sm:grid-cols-[120px_1fr] gap-3">
                    <div>
                      <Label className="text-xs text-zinc-400">Belt</Label>
                      <select
                        value={p.belt}
                        onChange={(e) => updatePhase(idx, { belt: e.target.value })}
                        className="mt-1 w-full bg-[#09090B] border border-zinc-700 text-white rounded px-2 py-1.5 text-sm"
                        data-testid={`phase-belt-${idx}`}
                      >
                        {BELT_OPTIONS.map((b) => <option key={b} value={b}>{b}</option>)}
                      </select>
                    </div>
                    <div>
                      <Label className="text-xs text-zinc-400">Phase title</Label>
                      <Input value={p.title} onChange={(e) => updatePhase(idx, { title: e.target.value })} className="mt-1 bg-[#09090B] border-zinc-700 text-white" data-testid={`phase-title-${idx}`} />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-zinc-400">Tagline</Label>
                    <Input value={p.tagline || ''} onChange={(e) => updatePhase(idx, { tagline: e.target.value })} className="mt-1 bg-[#09090B] border-zinc-700 text-white" data-testid={`phase-tagline-${idx}`} placeholder="One-line summary" />
                  </div>
                  <div>
                    <Label className="text-xs text-zinc-400">Focus</Label>
                    <Textarea rows={2} value={p.focus || ''} onChange={(e) => updatePhase(idx, { focus: e.target.value })} className="mt-1 bg-[#09090B] border-zinc-700 text-white" data-testid={`phase-focus-${idx}`} />
                  </div>
                  <div>
                    <Label className="text-xs text-zinc-400">Outcome</Label>
                    <Input value={p.outcome || ''} onChange={(e) => updatePhase(idx, { outcome: e.target.value })} className="mt-1 bg-[#09090B] border-zinc-700 text-white" data-testid={`phase-outcome-${idx}`} />
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-xs text-zinc-400 uppercase tracking-wider font-semibold">Challenges</Label>
                      <Button size="sm" variant="ghost" onClick={() => addChallenge(idx)} className="text-[#C0A062] hover:bg-[#C0A062]/10" data-testid={`add-challenge-${idx}`}>
                        <Plus className="w-3.5 h-3.5 mr-1" /> Add challenge
                      </Button>
                    </div>
                    <ul className="space-y-2">
                      {(p.challenges || []).length === 0 ? (
                        <li className="text-xs text-zinc-500">No challenges yet.</li>
                      ) : (p.challenges || []).map((c, cIdx) => (
                        <li key={c.id} className="grid grid-cols-[1fr_80px_auto] gap-2 items-center" data-testid={`challenge-${idx}-${cIdx}`}>
                          <Input
                            value={c.description}
                            onChange={(e) => updateChallenge(idx, cIdx, { description: e.target.value })}
                            placeholder="Challenge description"
                            className="bg-[#09090B] border-zinc-700 text-white text-sm"
                            data-testid={`challenge-desc-${idx}-${cIdx}`}
                          />
                          <Input
                            type="number" min="1" max="100"
                            value={c.xp_reward}
                            onChange={(e) => updateChallenge(idx, cIdx, { xp_reward: e.target.value })}
                            className="bg-[#09090B] border-zinc-700 text-white text-sm text-center"
                            data-testid={`challenge-xp-${idx}-${cIdx}`}
                          />
                          <button onClick={() => removeChallenge(idx, cIdx)} className="text-zinc-500 hover:text-red-400 p-1" aria-label="Remove" data-testid={`remove-challenge-${idx}-${cIdx}`}>
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </li>
                      ))}
                    </ul>
                    <p className="text-[10px] text-zinc-600 mt-1">Max 100 XP per challenge.</p>
                  </div>

                  <div className="flex justify-end pt-2">
                    <Button size="sm" variant="ghost" onClick={() => removePhase(idx)} className="text-red-300 hover:bg-red-500/10" data-testid={`remove-phase-${idx}`}>
                      <Trash2 className="w-4 h-4 mr-1" /> Delete phase
                    </Button>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        <Button onClick={addPhase} variant="outline" className="border-dashed border-zinc-700 text-zinc-300 hover:bg-zinc-800 w-full" data-testid="add-phase-btn">
          <Plus className="w-4 h-4 mr-2" /> Add phase
        </Button>
      </div>
    </div>
  );
};

export default CurriculumEditor;
